import { Routes } from '@angular/router';
import { DashboardComponent } from './dashboard/dashboard.component';

import { ProfileComponent } from './profile/profile.component';
import { ProfileEditComponent } from './profile/profile-edit.component';
import { CourseListComponent } from './courses/course-list.component';
import { CoursePlayerRouteWrapperComponent } from './courses/course-player-route-wrapper/course-player-route-wrapper.component';
import { CertificateViewerComponent } from './courses/certificate-viewer.component';
import { SimpleNotificationsComponent } from './notifications/simple-notifications.component';
import { MainLayoutComponent } from './shared/layouts/main-layout.component';
import { authGuard } from './shared/guards/auth.guard';
import { profileCompletionGuard } from './shared/guards/profile-completion.guard';

export const routes: Routes = [
  // Auth routes (WITHOUT sidebar)
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.routes').then(m => m.AUTH_ROUTES)
  },

  // PUBLIC tokenized signup from a new-user invitation email (Email Wave 2).
  // No auth guard: the recipient has no account yet — the ?token= query param
  // is the credential and is validated server-side.
  {
    path: 'signup/invite',
    loadComponent: () => import('./auth/signup-invite/signup-invite.component').then(m => m.SignupInviteComponent)
  },
  
  // Course player route - OUTSIDE layout to render full-screen modal.
  // profileCompletionGuard (KAN-562) holds a learner on /profile until the
  // fields their org asked for are filled. Guarding the route covers every way
  // in at once - invite redirect, dashboard click, deep link, hard reload. It
  // is a nudge, not a security boundary: learners only, and it fails open.
  {
    path: 'courses/:id',
    component: CoursePlayerRouteWrapperComponent,
    canActivate: [authGuard, profileCompletionGuard]
  },
  // Stripe Checkout return pages (KAN-353) - chrome-less, OUTSIDE layout.
  // The server pins success_url/cancel_url to exactly these paths; the
  // redirect is a hint only - the success page polls the payment status
  // until the fulfillment webhook lands.
  {
    path: 'payments/success',
    canActivate: [authGuard],
    loadComponent: () => import('./payments/payment-success/payment-success.component').then(m => m.PaymentSuccessComponent)
  },
  {
    path: 'payments/cancel',
    canActivate: [authGuard],
    loadComponent: () => import('./payments/payment-cancel/payment-cancel.component').then(m => m.PaymentCancelComponent)
  },
  // Org platform-subscription lockout page (KAN-356 Phase 8 task 8.4c) -
  // chrome-less, OUTSIDE layout. The EntitlementLockoutInterceptor routes here
  // on a 402 ORG_SUBSCRIPTION_REQUIRED / ORG_BILLING_PAST_DUE, passing
  // ?organizationId= & ?reason=. Dark-launch inert until an org opts in.
  {
    path: 'billing/locked',
    canActivate: [authGuard],
    loadComponent: () => import('./payments/billing-locked/billing-locked.component').then(m => m.BillingLockedComponent)
  },
  // PUBLIC register-and-buy purchase page (KAN-331 Phase 6). No auth guard:
  // the unguessable share token in the URL is the credential, validated
  // server-side (the signup/invite precedent). Signed-out visitors are
  // offered sign-in / register and return here via sessionStorage.returnUrl.
  {
    path: 'buy/:token',
    loadComponent: () => import('./payments/public-purchase/public-purchase.component').then(m => m.PublicPurchaseComponent)
  },
  {
    path: 'notifications',
    redirectTo: 'invitations',
    pathMatch: 'full'
  },

  // Error pages
  {
    path: 'access-denied',
    loadComponent: () => import('./shared/pages/access-denied/access-denied.component').then(m => m.AccessDeniedComponent)
  },
  {
    path: 'error',
    loadComponent: () => import('./shared/components/error-page/error-page.component').then(m => m.ErrorPageComponent),
    data: { errorType: '500' }
  },
  {
    path: 'maintenance',
    loadComponent: () => import('./shared/components/error-page/error-page.component').then(m => m.ErrorPageComponent),
    data: { errorType: 'maintenance' }
  },
  {
    path: 'offline',
    loadComponent: () => import('./shared/components/error-page/error-page.component').then(m => m.ErrorPageComponent),
    data: { errorType: 'offline' }
  },
  
  // Root path redirect - Check auth and redirect accordingly
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full'
  },
  
  // App shell (KAN-670). Every signed-in page that shows the sidebar and top bar
  // is a child of ONE route that renders MainLayoutComponent. The shell used to be
  // declared separately on each page's route, so every sidebar click moved to a
  // different route config and the router destroyed and rebuilt the whole shell -
  // the layout jump, a sidebar snapping back to its top, and every shell fetch
  // re-run. Do not give a child its own `component: MainLayoutComponent`.
  //
  // canActivateChild (not canActivate) so the session is re-checked on every page
  // change inside the shell, as it was when each page carried its own authGuard;
  // canActivate on a parent that stays mounted would run only once, on entry.
  //
  // This sits AFTER every chrome-less route above so `/courses/:id`, the
  // payment/billing pages and the error pages keep rendering without the shell.
  {
    path: '',
    component: MainLayoutComponent,
    canActivateChild: [authGuard],
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            component: ProfileComponent
          },
          {
            path: 'edit',
            component: ProfileEditComponent
          }
        ]
      },
      {
        path: 'my-credentials',
        loadComponent: () => import('./profile/my-credentials/my-credentials.component').then(m => m.MyCredentialsComponent)
      },
      {
        path: 'courses',
        component: CourseListComponent
      },
      {
        path: 'certificates/:id',
        component: CertificateViewerComponent
      },
      {
        path: 'bookmarks',
        loadComponent: () => import('./bookmarks/bookmarks.component').then(m => m.BookmarksComponent)
      },
      // Member course catalog (KAN-385) - the in-app way to discover and buy the
      // active org's purchasable courses. A member nav surface inside MainLayout.
      {
        path: 'catalog',
        loadComponent: () => import('./payments/course-catalog/course-catalog.component').then(m => m.CourseCatalogComponent)
      },
      // Member dues page (KAN-357 task 11.4b) - a member nav surface inside
      // MainLayout, unlike the chrome-less /payments/success. The dues checkout
      // returns here with ?checkout=success|canceled&session_id.
      {
        path: 'dues',
        loadComponent: () => import('./payments/dues-page/dues-page.component').then(m => m.DuesPageComponent)
      },
      // The /notifications page only renders pending course + org invitations,
      // so rename the route + sidebar label to "Invitations" for clarity
      // (UI polish audit #2). /notifications redirects here (below) so the
      // existing notification-bell link and any stored deep links keep working.
      {
        path: 'invitations',
        component: SimpleNotificationsComponent
      },
      {
        path: 'help',
        loadChildren: () => import('./help/help.routes').then(m => m.HELP_ROUTES)
      },
      {
        path: 'admin',
        loadChildren: () => import('./admin/admin.routes').then(m => m.ADMIN_ROUTES)
      },
      {
        path: 'instructor',
        loadChildren: () => import('./instructor/instructor.routes').then(m => m.INSTRUCTOR_ROUTES)
      }
    ]
  },
  // 404 - Page not found (wildcard route)
  {
    path: '**',
    loadComponent: () => import('./shared/components/error-page/error-page.component').then(m => m.ErrorPageComponent),
    data: { errorType: '404' }
  }
];
